import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { Loader2, Plus, Trash2, X } from '../components/icons';

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram', color: '#E1306C' },
  { id: 'tiktok',    label: 'TikTok',    color: '#25F4EE' },
];

const METRIC_FIELDS = [
  { key: 'followers',        label: 'Abonnés' },
  { key: 'following',        label: 'Abonnements' },
  { key: 'posts',            label: 'Publications' },
  { key: 'likes',            label: 'J\'aime' },
  { key: 'views',            label: 'Vues' },
  { key: 'comments',         label: 'Commentaires' },
  { key: 'shares',           label: 'Partages' },
  { key: 'saves',            label: 'Enregistrements' },
  { key: 'reach',            label: 'Portée' },
  { key: 'impressions',      label: 'Impressions' },
  { key: 'engagement_rate',  label: 'Engagement (%)' },
];

export default function TrackingPage() {
  const { user, isFounder } = useAuth();
  const [selectedClientId, setSelectedClientId] = useState(isFounder ? null : user?.id);
  const [clients, setClients] = useState([]);
  const [platform, setPlatform] = useState('instagram');
  const [entries, setEntries] = useState([]);
  const [weeklyRecap, setWeeklyRecap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showHandles, setShowHandles] = useState(false);
  // Iter61 — Apify refresh state machine (fire-and-forget + polling)
  const [refreshRun, setRefreshRun] = useState(null); // {status, run_id, ...}

  useEffect(() => {
    if (isFounder) {
      api.get('/tracking/clients/all').then((r) => {
        setClients(r.data || []);
        if (!selectedClientId && r.data?.length > 0) setSelectedClientId(r.data[0].id);
      }).catch(() => setClients([]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder]);

  useEffect(() => {
    if (!selectedClientId) { setEntries([]); setWeeklyRecap([]); setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get(`/tracking/${selectedClientId}?platform=${platform}`),
      api.get(`/tracking/${selectedClientId}/weekly-recap?platform=${platform}`),
    ]).then(([r1, r2]) => {
      setEntries(r1.data || []);
      setWeeklyRecap(r2.data || []);
    }).catch(() => { setEntries([]); setWeeklyRecap([]); })
    .finally(() => setLoading(false));
  }, [selectedClientId, platform]);

  const reload = () => {
    if (!selectedClientId) return;
    Promise.all([
      api.get(`/tracking/${selectedClientId}?platform=${platform}`),
      api.get(`/tracking/${selectedClientId}/weekly-recap?platform=${platform}`),
    ]).then(([r1, r2]) => {
      setEntries(r1.data || []);
      setWeeklyRecap(r2.data || []);
    }).catch(() => {});
  };

  // Iter61 — Poll refresh-status every 4s while a run is in progress
  useEffect(() => {
    if (!selectedClientId) { setRefreshRun(null); return; }
    let alive = true;
    api.get(`/tracking/${selectedClientId}/refresh-status`)
      .then((r) => { if (alive) setRefreshRun(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [selectedClientId]);

  useEffect(() => {
    if (!refreshRun || refreshRun.status !== 'running') return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/tracking/${selectedClientId}/refresh-status`);
        setRefreshRun(r.data);
        if (r.data.status === 'done') {
          const errs = r.data.result?.errors || [];
          if (errs.length) toast.error(`Sync partiel : ${errs.join(' · ')}`);
          else toast.success('Stats à jour depuis Apify ✓');
          reload();
        } else if (r.data.status === 'error') {
          toast.error('Sync Apify échoué');
        }
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRun?.status, refreshRun?.id, selectedClientId]);

  const triggerApifyRefresh = async () => {
    try {
      const r = await api.post(`/tracking/${selectedClientId}/refresh`);
      if (r.data.status === 'already_running') {
        toast('Sync déjà en cours…', { icon: '⏳' });
      } else {
        toast('Sync Apify lancée (≈ 1–2 min)…', { icon: '🚀' });
      }
      // Fetch status to start polling
      const s = await api.get(`/tracking/${selectedClientId}/refresh-status`);
      setRefreshRun(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh échoué');
    }
  };

  const currentClient = clients.find((c) => c.id === selectedClientId) ||
    (!isFounder ? { id: user?.id, name: user?.first_name || 'Moi' } : null);

  return (
    <div className="page-fade">
      <PageHeader
        title="Tracking"
        subtitle={isFounder ? 'Statistiques réseaux sociaux par client' : 'Mes statistiques Instagram & TikTok'}
        right={
          selectedClientId && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                data-testid="tracking-refresh-apify"
                onClick={triggerApifyRefresh}
                disabled={refreshRun?.status === 'running'}
                className="asa-btn-ghost asa-btn"
                title="Récupérer automatiquement les stats via Apify"
                style={refreshRun?.status === 'running' ? { opacity: 0.7, cursor: 'wait' } : undefined}
              >
                <Loader2 size={13} className={refreshRun?.status === 'running' ? 'spin' : ''} />
                {refreshRun?.status === 'running' ? 'Sync en cours…' : 'Sync auto'}
              </button>
              <button
                data-testid="tracking-handles"
                onClick={() => setShowHandles(true)}
                className="asa-btn-ghost asa-btn"
              >
                @ Pseudos
              </button>
              <button
                data-testid="tracking-add-entry"
                onClick={() => setShowAdd(true)}
                className="asa-btn"
              >
                <Plus size={14} /> Ajouter une donnée
              </button>
            </div>
          )
        }
      />

      <div style={{ padding: '0 clamp(12px, 4vw, 22px) 60px' }}>
        {/* Client selector (founders only) */}
        {isFounder && (
          <div style={{ marginBottom: 20 }}>
            <div className="asa-eyebrow" style={{ marginBottom: 8, fontSize: '0.6rem' }}>Client</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {clients.length === 0 ? (
                <div style={{ color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12 }}>Aucun client approuvé</div>
              ) : clients.map((c) => (
                <button
                  key={c.id}
                  data-testid={`tracking-client-${c.id}`}
                  onClick={() => setSelectedClientId(c.id)}
                  style={{
                    padding: '8px 14px', borderRadius: 999,
                    background: selectedClientId === c.id ? 'rgba(91,190,252,0.16)' : 'var(--sf)',
                    border: `1.5px solid ${selectedClientId === c.id ? 'rgba(91,190,252,0.50)' : 'var(--bd)'}`,
                    color: selectedClientId === c.id ? '#5BBEFC' : 'var(--t2)',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                  }}
                >{c.name}</button>
              ))}
            </div>
          </div>
        )}

        {/* Platform tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              data-testid={`tracking-platform-${p.id}`}
              onClick={() => setPlatform(p.id)}
              style={{
                padding: '9px 18px', borderRadius: 10,
                background: platform === p.id ? `${p.color}22` : 'var(--sf)',
                border: `1.5px solid ${platform === p.id ? p.color : 'var(--bd)'}`,
                color: platform === p.id ? p.color : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >{p.label}</button>
          ))}
        </div>

        {!selectedClientId ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--t3)' }}>
            Sélectionne un client
          </div>
        ) : loading ? (
          <div className="shimmer" style={{ height: 300, borderRadius: 14 }} />
        ) : entries.length === 0 ? (
          <div style={{
            padding: 40, textAlign: 'center', borderRadius: 14,
            background: 'var(--sf)', border: '1.5px dashed var(--bd)',
            fontFamily: 'Lexend', fontSize: 13, color: 'var(--t3)',
          }}>
            Aucune donnée {platform} pour {currentClient?.name || 'ce client'} encore.
            <br />Clique sur « + Ajouter une donnée » pour commencer.
          </div>
        ) : (
          <>
            <WeeklyRecapCard recap={weeklyRecap} platform={platform} />
            <EntriesTable entries={entries} platform={platform} onDelete={async (id) => {
              try { await api.delete(`/tracking/${id}`); reload(); }
              catch { toast.error('Erreur suppression'); }
            }} canDelete={isFounder} />
            <TrackingCharts entries={entries} platform={platform} />
          </>
        )}
      </div>

      {showAdd && selectedClientId && (
        <AddTrackingDialog
          clientId={selectedClientId}
          clientName={currentClient?.name}
          platform={platform}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); reload(); }}
        />
      )}

      {showHandles && selectedClientId && (
        <HandlesDialog
          clientId={selectedClientId}
          clientName={currentClient?.name}
          onClose={() => setShowHandles(false)}
        />
      )}
    </div>
  );
}

function HandlesDialog({ clientId, clientName, onClose }) {
  const [ig, setIg] = useState('');
  const [tt, setTt] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/users/${clientId}/social-handles`).then((r) => {
      setIg(r.data?.instagram_handle || '');
      setTt(r.data?.tiktok_handle || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);
  const save = async () => {
    try {
      await api.patch(`/users/${clientId}/social-handles`, {
        instagram_handle: ig, tiktok_handle: tt,
      });
      toast.success('Pseudos enregistrés');
      onClose();
    } catch { toast.error('Erreur'); }
  };
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{
        width: '100%', maxWidth: 420,
        background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 20, padding: 24,
      }}>
        <h2 className="asa-heading" style={{ margin: '0 0 4px', fontSize: 'var(--text-lg)' }}>Pseudos sociaux</h2>
        <p style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', margin: '4px 0 16px' }}>
          Pour {clientName} — utilisés par le sync auto Apify
        </p>
        {loading ? <Loader2 size={16} className="spin" /> : (
          <>
            <div className="asa-eyebrow" style={{ marginBottom: 6, fontSize: '0.55rem' }}>Instagram (sans @)</div>
            <input
              data-testid="handle-ig-input"
              value={ig}
              onChange={(e) => setIg(e.target.value)}
              placeholder="rayan.asa"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 14,
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
              }}
            />
            <div className="asa-eyebrow" style={{ marginBottom: 6, fontSize: '0.55rem' }}>TikTok (sans @)</div>
            <input
              data-testid="handle-tt-input"
              value={tt}
              onChange={(e) => setTt(e.target.value)}
              placeholder="rayan.asa"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 18,
                background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} className="asa-btn-ghost asa-btn" style={{ padding: '10px 16px' }}>Annuler</button>
              <button data-testid="handle-save" onClick={save} className="asa-btn" style={{ padding: '10px 18px' }}>Enregistrer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function WeeklyRecapCard({ recap, platform }) {
  if (!recap || recap.length === 0) return null;
  const entry = recap.find((r) => r.platform === platform);
  if (!entry || !entry.delta || Object.keys(entry.delta).length === 0) return null;
  const items = [
    { key: 'followers', label: 'Abonnés', icon: '👥' },
    { key: 'views', label: 'Vues', icon: '👁' },
    { key: 'likes', label: 'J\'aime', icon: '❤️' },
    { key: 'engagement_rate', label: 'Engagement', icon: '⚡', suffix: '%' },
  ].filter((it) => entry.delta[it.key] != null);
  if (items.length === 0) return null;

  return (
    <div
      className="asa-card"
      style={{
        padding: '18px 20px', marginBottom: 20,
        background: 'linear-gradient(135deg, rgba(91,190,252,0.10), rgba(167,139,250,0.08))',
        border: '1.5px solid rgba(91,190,252,0.30)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontFamily: 'Sora', fontSize: '0.75rem', fontWeight: 800, color: 'var(--t1)',
          textTransform: 'uppercase', letterSpacing: '0.10em',
        }}>Bilan de la semaine</span>
        <span style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>
          vs. il y a 7 jours
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {items.map((it) => {
          const v = entry.delta[it.key];
          const pct = entry.delta[it.key + '_pct'];
          const positive = v >= 0;
          return (
            <div key={it.key} style={{
              padding: 12, background: 'var(--sf)',
              borderRadius: 10, border: '1px solid var(--bd)',
            }}>
              <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginBottom: 4 }}>
                {it.icon} {it.label}
              </div>
              <div style={{
                fontFamily: 'Sora', fontSize: '1.2rem', fontWeight: 800,
                color: positive ? '#34D399' : '#FF6B6B',
              }}>
                {positive ? '+' : ''}{typeof v === 'number' ? v.toLocaleString('fr-FR') : v}{it.suffix || ''}
              </div>
              {pct != null && (
                <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>
                  {positive ? '+' : ''}{pct}%
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrackingCharts({ entries, platform }) {
  if (!entries || entries.length < 2) {
    return (
      <div style={{
        marginBottom: 24, padding: 18, borderRadius: 14,
        background: 'var(--sf)', border: '1.5px dashed var(--bd)',
        fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', textAlign: 'center',
      }}>
        Ajoute au moins 2 entrées pour voir les graphiques d&apos;évolution.
      </div>
    );
  }

  // Data prep: sort by date ASC, one point per date
  const data = [...entries].sort((a, b) => a.date.localeCompare(b.date)).map((e) => ({
    date: e.date.slice(5), // MM-DD for display
    ...e,
  }));

  // Which metrics are worth plotting (present in > 1 entry)
  const METRIC_CONFIG = [
    { key: 'followers',       label: 'Abonnés',       color: '#5BBEFC' },
    { key: 'views',           label: 'Vues',          color: '#A78BFA' },
    { key: 'likes',           label: 'J\'aime',       color: '#F5A623' },
    { key: 'engagement_rate', label: 'Engagement %',  color: '#34D399' },
  ];
  const plotted = METRIC_CONFIG.filter((m) => entries.filter((e) => e[m.key] != null).length >= 2);
  if (plotted.length === 0) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <div className="asa-eyebrow" style={{ marginBottom: 10, fontSize: '0.6rem' }}>
        Évolution · {platform === 'instagram' ? 'Instagram' : 'TikTok'}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12,
      }}>
        {plotted.map((m) => (
          <div key={m.key} className="asa-card" style={{ padding: 14 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.color }} />
              <span style={{
                fontFamily: 'Sora', fontSize: 11.5, fontWeight: 700, color: 'var(--t2)',
              }}>{m.label}</span>
              <div style={{ flex: 1 }} />
              <TrendBadge data={data} field={m.key} />
            </div>
            <ResponsiveContainer width="100%" height={130}>
              <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={m.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={m.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(148,163,184,0.10)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'var(--t3)', fontSize: 10, fontFamily: 'Lexend' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'var(--t3)', fontSize: 10, fontFamily: 'Lexend' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--sf)',
                    border: '1px solid var(--bd)', borderRadius: 8,
                    fontFamily: 'Lexend', fontSize: 12,
                  }}
                  labelStyle={{ color: 'var(--t3)' }}
                  formatter={(v) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : v)}
                />
                <Area type="monotone" dataKey={m.key} stroke={m.color} strokeWidth={2.2} fill={`url(#grad-${m.key})`} dot={{ r: 3, fill: m.color, strokeWidth: 0 }} activeDot={{ r: 5, fill: m.color }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendBadge({ data, field }) {
  const values = data.map((d) => d[field]).filter((v) => v != null);
  if (values.length < 2) return null;
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const pct = first > 0 ? Math.round((delta / first) * 100) : 0;
  const positive = delta >= 0;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999,
      background: positive ? 'rgba(52,211,153,0.14)' : 'rgba(255,107,107,0.14)',
      color: positive ? '#34D399' : '#FF6B6B',
      fontFamily: 'Sora', fontSize: 10.5, fontWeight: 800,
    }}>
      {positive ? '+' : ''}{pct}%
    </span>
  );
}

function EntriesTable({ entries, platform, onDelete, canDelete }) {
  // Show latest first
  const sorted = [...entries].reverse();
  const first = sorted[0] || {};
  // Non-empty metric fields in the latest entry
  const activeMetrics = METRIC_FIELDS.filter((m) => first[m.key] != null);

  return (
    <div>
      {/* Latest snapshot cards */}
      {activeMetrics.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 44%), 1fr))', gap: 10,
          marginBottom: 26,
        }}>
          {activeMetrics.map((m) => (
            <div key={m.key} className="asa-card" style={{ padding: 14 }}>
              <div style={{
                fontFamily: 'Sora', fontSize: 10, fontWeight: 700,
                color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.08em',
                marginBottom: 6,
              }}>{m.label}</div>
              <div style={{
                fontFamily: 'Sora', fontSize: '1.5rem', fontWeight: 800, color: 'var(--t1)',
              }}>
                {typeof first[m.key] === 'number' ? first[m.key].toLocaleString('fr-FR') : first[m.key]}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History table */}
      <div className="asa-eyebrow" style={{ marginBottom: 8, fontSize: '0.6rem' }}>
        Historique · {entries.length} entrée{entries.length > 1 ? 's' : ''}
      </div>
      <div className="asa-card" style={{ overflow: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Lexend', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--sf2)' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--t3)', fontWeight: 600 }}>Date</th>
              {METRIC_FIELDS.filter((m) => entries.some((e) => e[m.key] != null)).map((m) => (
                <th key={m.key} style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--t3)', fontWeight: 600 }}>{m.label}</th>
              ))}
              {canDelete && <th style={{ width: 40 }} />}
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => (
              <tr key={e.id} style={{ borderTop: '1px solid var(--bd)' }}>
                <td style={{ padding: '10px 12px', color: 'var(--t1)', fontWeight: 600 }}>{e.date}</td>
                {METRIC_FIELDS.filter((m) => entries.some((x) => x[m.key] != null)).map((m) => (
                  <td key={m.key} style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--t2)' }}>
                    {e[m.key] != null ? (typeof e[m.key] === 'number' ? e[m.key].toLocaleString('fr-FR') : e[m.key]) : '—'}
                  </td>
                ))}
                {canDelete && (
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button
                      onClick={() => window.confirm('Supprimer cette entrée ?') && onDelete(e.id)}
                      data-testid={`tracking-delete-${e.id}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}
                    ><Trash2 size={13} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddTrackingDialog({ clientId, clientName, platform, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    followers: '', posts: '', likes: '', views: '', comments: '', shares: '', engagement_rate: '',
  });

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { client_id: clientId, platform, date: form.date };
      for (const k of Object.keys(form)) {
        if (k === 'date') continue;
        const v = form[k].toString().trim();
        if (v) payload[k] = k === 'engagement_rate' ? parseFloat(v) : parseInt(v, 10);
      }
      await api.post('/tracking', payload);
      toast.success('Donnée ajoutée');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(10,26,51,0.70)', backdropFilter: 'blur(8px)',
      display: 'grid', placeItems: 'center', padding: 18,
    }}>
      <form
        onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 500,
          background: 'var(--sf)', border: '1.5px solid var(--bd)', borderRadius: 20, padding: 24,
          maxHeight: '88vh', overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <h2 className="asa-heading" style={{ margin: 0, fontSize: 'var(--text-lg)', flex: 1 }}>
            Ajouter une donnée {platform === 'instagram' ? 'Instagram' : 'TikTok'}
          </h2>
          <button type="button" onClick={onClose}><X size={18} color="var(--t3)" /></button>
        </div>
        <p style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 4, marginBottom: 16 }}>
          Pour {clientName}
        </p>

        <div className="asa-eyebrow" style={{ marginBottom: 6, fontSize: '0.55rem' }}>Date</div>
        <input
          type="date"
          data-testid="tracking-form-date"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          required
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 14,
            background: 'var(--sf2)', border: '1.5px solid var(--bd)',
            fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', colorScheme: 'dark',
          }}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { k: 'followers', l: 'Abonnés' },
            { k: 'posts', l: 'Publications' },
            { k: 'likes', l: 'J\'aime' },
            { k: 'views', l: 'Vues' },
            { k: 'comments', l: 'Commentaires' },
            { k: 'shares', l: 'Partages' },
            { k: 'engagement_rate', l: 'Engagement %' },
          ].map(({ k, l }) => (
            <div key={k}>
              <div className="asa-eyebrow" style={{ marginBottom: 5, fontSize: '0.55rem' }}>{l}</div>
              <input
                type="number"
                step={k === 'engagement_rate' ? '0.01' : '1'}
                data-testid={`tracking-form-${k}`}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder="—"
                style={{
                  width: '100%', padding: '9px 12px', borderRadius: 8,
                  background: 'var(--sf2)', border: '1.5px solid var(--bd)',
                  fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t1)',
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button type="button" onClick={onClose} className="asa-btn-ghost asa-btn" style={{ flex: 1 }}>Annuler</button>
          <button data-testid="tracking-form-submit" type="submit" disabled={busy} className="asa-btn" style={{ flex: 1 }}>
            {busy ? <Loader2 size={15} className="spin" /> : 'Ajouter'}
          </button>
        </div>
      </form>
    </div>
  );
}
